import { apiErrorMessage } from '@/api/client';
import { documentsApi } from '@/api/documents.api';
import { Modal } from '@/components/common/Modal';
import { CheckIcon, CopyIcon } from '@/components/common/icons';
import type { ExportMode } from '@/types/documents.types';
import type { FC } from 'react';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

interface CopyTextButtonProps {
  documentId: string;
  /** The preview's mode: the copy is always the variant the preview shows, Originál included. */
  mode: ExportMode;
  /** Held from outside (the text endpoint answers 409 while the skipped pages are re-run). */
  disabled?: boolean;
}

/** How long the button stays green with "Zkopírováno", as in the prototype. */
const COPIED_MS = 1800;

/**
 * "Kopírovat text": the document's text in the previewed variant, without running headers and
 * footers (`GET /documents/{id}/text?mode=`), onto the clipboard. The button turns green with
 * "Zkopírováno" for 1.8 s.
 *
 * The clipboard API only exists in a secure context (https or localhost) and may be refused, so a
 * failure opens the text in a read-only field instead, already selected, for a plain Ctrl+C.
 */
export const CopyTextButton: FC<CopyTextButtonProps> = ({ documentId, mode, disabled = false }) => {
  const { t } = useTranslation();
  const titleId = useId();
  const [isCopying, setIsCopying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [fallbackText, setFallbackText] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = async () => {
    setIsCopying(true);
    let text: string;
    try {
      text = await documentsApi.text(documentId, mode);
    } catch (err) {
      toast.error(apiErrorMessage(err, t('review.copyText.failed')));
      setIsCopying(false);
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      clearTimeout(timer.current);
      setCopied(true);
      timer.current = setTimeout(() => setCopied(false), COPIED_MS);
    } catch {
      // `navigator.clipboard` is undefined outside a secure context, which lands here as well.
      setFallbackText(text);
    } finally {
      setIsCopying(false);
    }
  };

  const label = copied ? t('review.copyText.copied') : t('review.copyText.button');

  return (
    <>
      <button
        type="button"
        onClick={copy}
        disabled={isCopying || disabled}
        title={t(`review.copyText.title.${mode}`)}
        aria-label={label}
        className={`flex cursor-pointer items-center gap-[7px] whitespace-nowrap rounded border px-3 py-2 text-[13px] font-semibold hover:border-primary hover:text-primary disabled:cursor-progress ${
          copied ? 'border-ok bg-ok-soft text-ok-deep' : 'border-control bg-white text-muted'
        }`}
      >
        {copied ? <CheckIcon size={14} strokeWidth={2.6} /> : <CopyIcon size={14} />}
        <span className="max-[1040px]:hidden">{label}</span>
      </button>

      {fallbackText !== null ? (
        <Modal onClose={() => setFallbackText(null)} maxWidth={640} labelledBy={titleId} className="p-7 pb-[22px]">
          <div id={titleId} className="mb-2 text-base font-bold text-primary">
            {t('review.copyText.fallbackTitle')}
          </div>
          <p className="m-0 mb-3 text-[13px] leading-[1.6] text-muted">{t('review.copyText.fallbackHint')}</p>
          <textarea
            readOnly={true}
            value={fallbackText}
            rows={14}
            aria-label={t('review.copyText.fallbackLabel')}
            onFocus={(event) => event.currentTarget.select()}
            // biome-ignore lint/a11y/noAutofocus: the dialog exists only to copy this text
            autoFocus={true}
            className="block w-full border border-line bg-surface p-3 font-mono text-xs text-ink focus:border-primary focus:outline-none"
          />
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={() => setFallbackText(null)}
              className="cursor-pointer rounded border border-control bg-white px-3.5 py-[9px] text-[12.5px] font-semibold text-muted hover:border-primary hover:text-primary"
            >
              {t('common.close')}
            </button>
          </div>
        </Modal>
      ) : null}
    </>
  );
};
